import { motion } from 'framer-motion';
import { Mail, MapPin, Phone } from 'lucide-react';
import config from '../../config';
import { useSiteContent } from '../../lib/siteContent';

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

function telHref(phone) {
  const d = String(phone || "").replace(/\D/g, "");
  return d ? `tel:+${d}` : "";
}

function PhoneDisplay({ phone }) {
  const parts = String(phone || "").trim().split(/\s+/).filter(Boolean);
  const colors = ["#000000", "#672E8E", "#F47B20", "#672E8E"];
  if (parts.length >= 2) {
    return (
      <div className="text-xs whitespace-nowrap" style={{ fontFamily: "Montserrat" }}>
        {parts.map((p, i) => (
          <span key={`${p}-${i}`} style={{ color: colors[i] || "#672E8E" }}>
            {i ? " " : ""}{p}
          </span>
        ))}
      </div>
    );
  }
  return <div className="text-xs" style={{ fontFamily: "Montserrat" }}>{phone}</div>;
}

export default function ContactSection() {
  const site = useSiteContent();
  const contact = site.contact || {};
  const email = contact.email || config.SUPPORT_EMAIL;
  const phone = contact.phone || "";
  const phoneLink = telHref(phone);

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:items-stretch">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#f37721]" style={{ fontFamily: "Montserrat" }}>{contact.eyebrow}</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#311b52] mt-4 mb-4" style={{ fontFamily: "Poppins" }}>
              {contact.headline}
            </h2>
            <p className="text-base text-[#3e195e]/70 mb-8 leading-relaxed" style={{ fontFamily: "Montserrat" }}>
              {contact.body}
            </p>

            <div className="space-y-2.5">
              {config.WHATSAPP_CONFIGURED && (
                <a href={config.getWhatsAppLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn"
                  className="flex items-center gap-4 py-3 px-4 rounded-2xl bg-[#5f7a5a]/8 border border-[#8fbc8f]/20 hover:bg-[#5f7a5a]/12 transition-colors group">
                  <div className="w-11 h-11 rounded-xl bg-[#25D366] flex items-center justify-center shrink-0">
                    <WhatsAppIcon />
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-[#311b52]" style={{ fontFamily: "Poppins" }}>{contact.whatsapp_label || "WhatsApp Concierge"}</div>
                    <div className="text-xs text-[#673c96]/60" style={{ fontFamily: "Montserrat" }}>{contact.whatsapp_hint}</div>
                  </div>
                </a>
              )}

              {email && (
                <a href={`mailto:${email}`} data-testid="contact-email"
                  className="flex items-center gap-4 py-3 px-4 rounded-2xl bg-[#f5f3f7]/60 border border-[#673c96]/8 hover:bg-[#673c96]/8 transition-colors">
                  <div className="w-11 h-11 rounded-xl bg-[#3e195e] flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-[#311b52]" style={{ fontFamily: "Poppins" }}>Email</div>
                    <div className="text-xs text-[#673c96]/60" style={{ fontFamily: "Montserrat" }}>{email}</div>
                  </div>
                </a>
              )}

              {phone && (
                <a href={phoneLink || undefined} data-testid="contact-phone"
                  className="bmj-tel-mobile-only flex items-center gap-4 py-3 px-4 rounded-2xl bg-[#5f7a5a]/8 border border-[#8fbc8f]/20">
                  <div className="w-11 h-11 rounded-xl bg-[#3e195e] flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-[#311b52]" style={{ fontFamily: "Poppins" }}>Phone</div>
                    <PhoneDisplay phone={phone} />
                  </div>
                </a>
              )}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            className="bg-gradient-to-br from-[#311b52] to-[#673c96] rounded-3xl p-10 text-white">
            <MapPin className="w-8 h-8 text-[#f37721] mb-5" />
            <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "Poppins" }}>{contact.serving_title}</h3>
            <p className="text-white/75 text-sm leading-relaxed mb-6" style={{ fontFamily: "Montserrat" }}>
              {contact.serving_body}
            </p>
            <div className="border-t border-white/15 pt-6">
              <p className="text-white/50 text-xs mb-3 tracking-wider uppercase" style={{ fontFamily: "Montserrat" }}>{contact.hours_label}</p>
              <p className="text-sm font-medium">{contact.hours_weekday}</p>
              <p className="text-sm font-medium mt-1">{contact.hours_sunday}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
